/**
 * src/components/tapInvoiceViewer.js
 *
 * Shared inline viewer for scanned vendor invoices stored in Bottomline TAP.
 * JDE hands us a Media_Object_URL (…index.fsp?pn=DOLViewDocumentPS&o=<docId>);
 * the tapInvoiceDoc Azure Function replays TAP's redirect chain server-side and
 * streams the raw PDF, so we render it inline instead of bouncing the user to
 * TAP's own frameset viewer.
 *
 * Used by the Billing tab and the third-party forecast tabs (Materials,
 * Rentals, Subcontractors, All Third Party) at the PO-line drill level.
 *
 * Usage:
 *   const { openTapDoc, viewerNode } = useTapInvoiceViewer();
 *   ...
 *   {mediaUrl ? <a href={tapProxyUrl(mediaUrl)}
 *        onClick={(e) => { e.preventDefault(); openTapDoc(mediaUrl, label); }}>View</a> : null}
 *   ...
 *   {viewerNode}   // render once, near the component root
 */
import React, { useState, useEffect, useCallback, createElement } from 'react';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { loadPOMatchStatus, pNum } from '../db/company';

// Same env-aware Azure-Functions base URL as the other function callers
// (cologDocsApi / AISummary / billing).
export const TAP_API_BASE = (
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
        ? 'https://forecastingprodservice.azurewebsites.net/'
        : 'https://forecastingtestservice.azurewebsites.net/')
).replace(/\/?$/, '/');

// Pull the TAP document id (e.g. 18-3422079-1) out of a Media_Object_URL.
export const tapDocId = (u) => (/[?&]o=(\d{1,3}-\d{1,12}-\d{1,4})\b/.exec(u || '')?.[1] || '');

// Proxy URL that streams the PDF; '' when the link carries no parseable id.
export const tapProxyUrl = (u) => {
    const id = tapDocId(u);
    return id ? `${TAP_API_BASE}api/tapInvoiceDoc?o=${id}` : '';
};

// Hook that owns the viewer state and renders the modal. Returns:
//   openTapDoc(mediaObjectUrl, label) — open the inline viewer (falls back to
//     TAP's own viewer in a new tab when the link has no parseable doc id)
//   viewerNode — the modal element; render it once near the component root
export function useTapInvoiceViewer() {
    const [viewerDoc, setViewerDoc] = useState(null); // { proxy, tap, label }

    const openTapDoc = useCallback((tapUrl, label) => {
        const proxy = tapProxyUrl(tapUrl);
        if (proxy) setViewerDoc({ proxy, tap: tapUrl, label: label || 'Vendor invoice' });
        else if (tapUrl) window.open(tapUrl, '_blank', 'noopener,noreferrer');
    }, []);

    // Close on Escape.
    useEffect(() => {
        if (!viewerDoc) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setViewerDoc(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [viewerDoc]);

    const viewerNode = viewerDoc ? (
        <Pressable
            onPress={() => setViewerDoc(null)}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 60,
                backgroundColor: 'rgba(15, 23, 42, 0.6)', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', padding: 20,
            }}
        >
            <Pressable
                onPress={(e) => e.stopPropagation && e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: 8, width: 940, maxWidth: '96vw', height: '92vh',
                    boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)', cursor: 'default', overflow: 'hidden',
                }}
            >
                <View style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 20, paddingRight: 16, borderBottomWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', alignItems: 'center', flexShrink: 0 }}>
                    <View style={{ flexGrow: 1, flexShrink: 1, marginRight: 12 }}>
                        <Text style={{ fontSize: 15, fontWeight: '700', color: '#0f172a' }}>Vendor invoice</Text>
                        {viewerDoc.label ? (
                            <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }} numberOfLines={1}>{viewerDoc.label}</Text>
                        ) : null}
                    </View>
                    <Pressable onPress={() => setViewerDoc(null)}>
                        <Text style={{ fontSize: 20, color: '#64748b' }}>×</Text>
                    </Pressable>
                </View>
                <View style={{ flexGrow: 1, backgroundColor: '#f1f5f9' }}>
                    {createElement('iframe', {
                        src: viewerDoc.proxy,
                        title: 'Vendor invoice PDF',
                        style: { width: '100%', height: '100%', border: 'none', display: 'block' },
                    })}
                </View>
            </Pressable>
        </Pressable>
    ) : null;

    return { openTapDoc, viewerNode, hasViewer: !!viewerDoc };
}

// Loads the PO vendor-invoice match rollup for a job and returns it keyed
// `${PO_Number}|${PO_Line_Number}` (normalized with pNum, same as the Billing
// tab). Each entry carries a `TAP_URL` when a scanned invoice was matched.
export function usePoMatchStatus(jobId) {
    const [poStatus, setPoStatus] = useState({});
    useEffect(() => {
        let stale = false;
        setPoStatus({});
        if (!jobId) return undefined;
        loadPOMatchStatus({ id: jobId }).then((res) => {
            if (stale) return;
            const rows = res?.data || [];
            const map = {};
            (Array.isArray(rows) ? rows : []).forEach((r) => {
                map[`${pNum(r.PO_Number)}|${pNum(r.PO_Line_Number)}`] = r;
            });
            setPoStatus(map);
        }).catch(() => { /* no rollup → links just don't show */ });
        return () => { stale = true; };
    }, [jobId]);
    return poStatus;
}

// Resolve the TAP invoice link for a cost/commitment leaf row. Prefers the
// row's own Media_Object_URL (rare — usually empty), else the PO-match rollup's
// TAP_URL for this PO line. Returns '' when there's nothing to show.
export const rowTapUrl = (detail, poStatus) => {
    if (!detail) return '';
    if (detail.Media_Object_URL) return detail.Media_Object_URL;
    const po = detail.PO_Number;
    const line = detail.Line_ID;
    if (po == null || po === '' || line == null || line === '') return '';
    return poStatus?.[`${pNum(po)}|${pNum(line)}`]?.TAP_URL || '';
};

// JDE match statuses where AP has vouchered the vendor's invoice on the line.
const VOUCHERED_STATUSES = new Set(['Vouchered', 'Partially Vouchered']);

// Index the PO-match rollup for vendor-invoice lookups: the vouchered
// `PO|Line` keys, plus every PO with at least one vouchered line (for
// PO-level ETC entries, which carry no line).
export const invoicedIndex = (poStatus) => {
    const lines = new Set();
    const pos = new Set();
    Object.entries(poStatus || {}).forEach(([key, r]) => {
        if (!VOUCHERED_STATUSES.has(r?.Match_Status)) return;
        lines.add(key);
        pos.add(key.split('|')[0]);
    });
    return { lines, pos };
};

// True when a vendor invoice has come in against a cost / commitment / ETC
// row: its PO line is vouchered in JDE, it's a direct (non-PO) voucher, or it
// carries its own scanned-invoice link.
export const rowHasVendorInvoice = (row, index) => {
    if (!row) return false;
    if (row.Media_Object_URL || String(row.Document_Type || '').trim() === 'PV') return true;
    const po = row.PO_Number;
    if (po == null || po === '' || po === 'Unknown PO') return false;
    const line = row.Line_ID;
    if (line == null || line === '') return !!index?.pos.has(String(pNum(po)));
    return !!index?.lines.has(`${pNum(po)}|${pNum(line)}`);
};
